import type { DataRefreshEvent } from "@fp/admin-api-client";
import type { HubConnection } from "@microsoft/signalr";
import { useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useUserContext } from "@/components/user-context";
import { receiveDeployedVersion } from "@/lib/application-version";
import { createHubConnection } from "@/lib/signalr";
import { useSignalRNotifications } from "./useSignalRNotifications";

const RECEIVE_APPLICATION_VERSION = "ReceiveApplicationVersion";
const RECEIVE_DATA_REFRESH = "ReceiveDataRefresh";

const SignalRContext = createContext<HubConnection | null>(null);

function SignalREventHandlers() {
  useSignalRNotifications();
  return null;
}

export function SignalRProvider({ children }: { children: ReactNode }) {
  const { isInitialized, claims } = useUserContext();
  const queryClient = useQueryClient();
  const connectionRef = useRef<HubConnection | null>(null);
  const [connection, setConnection] = useState<HubConnection | null>(null);

  useEffect(() => {
    if (!isInitialized || !claims) return;

    const hubConnection = createHubConnection();
    connectionRef.current = hubConnection;
    let isStopped = false;

    hubConnection.on(RECEIVE_APPLICATION_VERSION, (version: string) => {
      // The Vite dev server serves source rather than a build. A local tab is never running a stale release.
      if (import.meta.env.DEV) return;
      receiveDeployedVersion(version);
    });

    hubConnection.onreconnected(() => {
      queryClient.invalidateQueries();
    });

    hubConnection
      .start()
      .then(() => {
        setConnection(hubConnection);
      })
      .catch((error: unknown) => {
        if (isStopped) return;
        console.error("SignalR connection failed:", error);
      });

    return () => {
      isStopped = true;
      connectionRef.current = null;
      setConnection(null);
      hubConnection.stop();
    };
  }, [isInitialized, claims, queryClient]);

  return (
    <SignalRContext.Provider value={connection}>
      <SignalREventHandlers />
      {children}
    </SignalRContext.Provider>
  );
}

export function useSignalRConnection(): HubConnection | null {
  return useContext(SignalRContext);
}

export function useDataRefresh<TPayload = undefined>(
  dataRefreshEvent: DataRefreshEvent,
  callback: (payload: TPayload) => void,
) {
  const connection = useSignalRConnection();
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  const stableCallback = useCallback(
    (receivedEvent: DataRefreshEvent, payload: unknown) => {
      if (receivedEvent !== dataRefreshEvent) return;
      callbackRef.current(payload as TPayload);
    },
    [dataRefreshEvent],
  );

  useEffect(() => {
    if (!connection) return;
    connection.on(RECEIVE_DATA_REFRESH, stableCallback);
    return () => connection.off(RECEIVE_DATA_REFRESH, stableCallback);
  }, [connection, stableCallback]);
}